import { IconPlus, IconRefresh, IconTerminal2 } from "@tabler/icons-react";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import type { SortingState } from "@tanstack/react-table";
import { Button } from "antd";
import { useEffect } from "react";
import { useAgentsPaged } from "@/common/artemis-operations/agents";
import {
	cardSortToKey,
	keyToSort,
} from "@/common/artemis-operations/use-paged-list";
import type { AgentStatus } from "@/common/model";
import {
	AgentHealthBadge,
	AgentStatusBadge,
	AgentStatusLabelMap,
} from "@/components/agents/agent-badges";
import {
	BasePageContainer,
	CustomEmpty,
	CustomIcon,
	QueryGuard,
} from "@/components/base";
import { CardBody, CardViewer } from "@/components/card-viewer";
import { useActions } from "@/contexts/actions-context";
import { usePageTitle } from "@/contexts/page-title-context";
import { useWizards } from "@/contexts/wizard-context";

export const Route = createFileRoute(
	"/_authenticated/$orgSlug/workspaces/$workspaceId/agents/",
)({
	component: Agents,
});

const asTime = (v: unknown) => new Date(v as string).getTime();

const labelOptions = (map: Record<string, string>) =>
	Object.entries(map).map(([value, label]) => ({ value, label }));

function Agents() {
	const navigate = useNavigate();
	const { setTitle } = usePageTitle();
	const { setActions } = useActions();
	const { orgSlug, workspaceId } = Route.useParams();
	const { openAgentCreateWizard, openCliSetupWizard } = useWizards();

	const list = useAgentsPaged(workspaceId);

	useEffect(() => {
		setTitle("Agents");
		setActions([
			{
				type: "button",
				id: "cli-setup",
				tooltip: "Set up the stratonext CLI",
				icon: <CustomIcon icon={IconTerminal2} />,
				onClick: () => openCliSetupWizard(),
			},
			{
				type: "button",
				id: "refresh",
				icon: <CustomIcon icon={IconRefresh} />,
				onClick: () => list.refetch(),
			},
			{
				type: "button",
				id: "create-agent",
				tooltip: "New Agent",
				icon: <CustomIcon icon={IconPlus} />,
				onClick: () => openAgentCreateWizard(),
			},
		]);
	}, [
		setTitle,
		setActions,
		openAgentCreateWizard,
		openCliSetupWizard,
		list.refetch,
	]);

	const noFilters = !list.search && !list.filters.status;

	if (!list.loading && !list.isError && list.totalCount === 0 && noFilters) {
		return (
			<BasePageContainer>
				<CustomEmpty description="agents" expanded>
					<Button type="primary" onClick={() => openAgentCreateWizard()}>
						Create Agent
					</Button>
				</CustomEmpty>
			</BasePageContainer>
		);
	}

	return (
		<QueryGuard isPending={list.loading} isError={list.isError}>
			<BasePageContainer>
				<CardViewer
					items={list.rows}
					getKey={(a) => a.id}
					server={{
						search: list.search,
						onSearchChange: list.setSearch,
						filterValues: { status: list.filters.status },
						onFilterChange: (k, v) => list.setFilter(k, v),
						sortValue: cardSortToKey(list.sort),
						onSortChange: (v) =>
							list.setSort(keyToSort[v] ?? ([] as SortingState)),
						totalCount: list.totalCount,
						hasNextPage: list.hasNextPage,
						hasPreviousPage: list.hasPreviousPage,
						onNextPage: list.onNextPage,
						onPreviousPage: list.onPreviousPage,
						pageSize: 50,
						pageIndex: list.pageIndex,
					}}
					onOpen={(a) =>
						navigate({
							to: "/$orgSlug/workspaces/$workspaceId/agents/$agentId",
							params: { orgSlug, workspaceId, agentId: a.id },
						})
					}
					searchText={(a) => `${a.name} ${a.endpointUrl ?? ""}`}
					searchPlaceholder="Search agents"
					filters={[
						{
							key: "status",
							allLabel: "All statuses",
							options: labelOptions(AgentStatusLabelMap),
							predicate: (a, v) => a.status === (v as AgentStatus),
						},
					]}
					sorts={[
						{
							value: "newest",
							label: "Newest",
							compare: (a, b) => asTime(b.createdAt) - asTime(a.createdAt),
						},
						{
							value: "oldest",
							label: "Oldest",
							compare: (a, b) => asTime(a.createdAt) - asTime(b.createdAt),
						},
						{
							value: "name",
							label: "Name A–Z",
							compare: (a, b) => a.name.localeCompare(b.name),
						},
					]}
					renderCard={(a) => (
						<CardBody
							title={a.name}
							extra={
								// External agents have no health (unreachable by the platform), so they
								// show their enable/disable state instead.
								a.health ? (
									<AgentHealthBadge status={a.health} />
								) : (
									<AgentStatusBadge status={a.status} />
								)
							}
							description={
								a.kind === "EXTERNAL"
									? `External, ${a.environment ? a.environment.name : "Self-managed"}`
									: `${a.endpointUrl !== undefined ? "A2A, " : ""} ${a.environment ? a.environment.name : "StratoNext"}`
							}
						/>
					)}
				/>
			</BasePageContainer>
		</QueryGuard>
	);
}
